/**
 * Single source of truth for the password policy — mirrors the Cognito
 * user-pool policy in `infrastructure/lib/stacks/CognitoStack.ts`
 * (minLength 12 + lowercase + uppercase + digits + symbols).
 *
 * Both web and mobile validate against THIS so the client-side check never
 * disagrees with Cognito's server-side rejection (which previously let mobile
 * accept 8-char / no-complexity passwords that Cognito then bounced with a
 * generic error). Pure logic — no React, no i18n (callers map requirement keys
 * to their own localized labels/messages).
 */
export const PASSWORD_MIN_LENGTH = 12;

export type PasswordRequirementKey =
  'length' | 'uppercase' | 'lowercase' | 'number' | 'special';

/** Ordered requirements, each with a pure predicate. */
export const PASSWORD_REQUIREMENTS: ReadonlyArray<{
  key: PasswordRequirementKey;
  test: (password: string) => boolean;
}> = [
  { key: 'length', test: password => password.length >= PASSWORD_MIN_LENGTH },
  { key: 'uppercase', test: password => /[A-Z]/.test(password) },
  { key: 'lowercase', test: password => /[a-z]/.test(password) },
  { key: 'number', test: password => /\d/.test(password) },
  {
    key: 'special',
    test: password => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  },
];

/** Requirement keys the password does NOT yet satisfy (in policy order). */
export function getUnmetPasswordRequirements(
  password: string
): PasswordRequirementKey[] {
  return PASSWORD_REQUIREMENTS.filter(req => !req.test(password)).map(
    req => req.key
  );
}

/** True when the password satisfies every requirement. */
export function isPasswordValid(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every(req => req.test(password));
}
